import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layout
import { MainLayout } from "./components/layout/MainLayout";

// Pages
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { TrainingProjectsPage } from "./pages/TrainingProjectsPage";
import { TrainingProjectFormPage } from "./pages/TrainingProjectFormPage";
import { TrainingProjectDetailPage } from "./pages/TrainingProjectDetailPage";
import { PartnersPage } from "./pages/PartnersPage";
import { CoursesPage } from "./pages/CoursesPage";
import { UsersPage } from "./pages/UsersPage";
import { TSPsPage } from "./pages/TSPsPage";
import { PurchaseOrdersPage } from "./pages/PurchaseOrdersPage";
import { TrainersPage } from "./pages/TrainersPage";
import { TrainerProfilePage } from "./pages/TrainerProfilePage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { ServiceRequestsPage } from "./pages/ServiceRequestsPage";
import { AllSessionsPage } from "./pages/AllSessionsPage";
import { HelpCenterPage } from "./pages/HelpCenterPage";
import { TermsOfServicePage } from "./pages/TermsOfServicePage";
import { PartnerConferencesPage } from "./pages/PartnerConferencesPage";
import { PartnerConferenceFormPage } from "./pages/PartnerConferenceFormPage";
import { PartnerConferenceDetailPage } from "./pages/PartnerConferenceDetailPage";
import { RegionalInPersonPage } from "./pages/RegionalInPersonPage";
import { RegionalInPersonFormPage } from "./pages/RegionalInPersonFormPage";
import { RegionalInPersonDetailPage } from "./pages/RegionalInPersonDetailPage";
import { PPRWPage } from "./pages/PPRWPage";
import { PPRWDetailPage } from "./pages/PPRWDetailPage";
import { PPRWWorkshopDetailPage } from "./pages/PPRWWorkshopDetailPage";
import { PPRWTemplateManagerPage } from "./pages/PPRWTemplateManagerPage";
import { ContentDevelopmentDashboardPage } from "./pages/ContentDevelopmentDashboardPage";
import { ContentItemsPage } from "./pages/ContentItemsPage";
import { ContentItemDetailPage } from "./pages/ContentItemDetailPage";
import { ContentProjectDetailPage } from "./pages/ContentProjectDetailPage";
import { ContentPOsPage } from "./pages/ContentPOsPage";
import { TaxonomyPage } from "./pages/TaxonomyPage";
import { WorkbackTemplateSettingsPage } from "./pages/WorkbackTemplateSettingsPage";
import { PlatformPage } from "./pages/PlatformPage";
import { TaskDetailPage } from "./pages/TaskDetailPage";
import { KnowledgeBasePage } from "./pages/KnowledgeBasePage";
import { MyOpenTasksPage } from "./pages/MyOpenTasksPage";

// Auth callback component for handling redirect from Azure AD
const AuthCallback = () => {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// The Training Delivery Dashboard is an org-wide Ops command center — Admins/Super Admins only.
// Non-admins are sent to their default landing (Private Deliveries).
const RequireAdmin = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return isAdmin() ? children : <Navigate to="/my-tasks" replace />;
};

// Role-aware landing for "/" and unknown routes.
const RoleLanding = () => {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={isAdmin() ? "/dashboard" : "/my-tasks"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Protected Routes */}
          <Route element={<MainLayout />}>
            <Route path="/my-tasks" element={<MyOpenTasksPage />} />
            <Route path="/dashboard" element={<RequireAdmin><DashboardPage /></RequireAdmin>} />
            <Route path="/training-projects" element={<TrainingProjectsPage />} />
            <Route path="/training-projects/new" element={<TrainingProjectFormPage />} />
            <Route path="/training-projects/:id" element={<TrainingProjectDetailPage />} />
            <Route path="/training-projects/:id/edit" element={<TrainingProjectDetailPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/tsps" element={<TrainersPage />} />
            <Route path="/trainers" element={<TrainersPage />} />
            <Route path="/trainers/:trainerId" element={<TrainerProfilePage />} />
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/sessions" element={<AllSessionsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/service-requests" element={<ServiceRequestsPage />} />
            <Route path="/help-center" element={<HelpCenterPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            {/* Partner Conferences */}
            <Route path="/partner-conferences" element={<PartnerConferencesPage />} />
            <Route path="/partner-conferences/new" element={<PartnerConferenceFormPage />} />
            <Route path="/partner-conferences/:id" element={<PartnerConferenceDetailPage />} />
            <Route path="/partner-conferences/:id/edit" element={<PartnerConferenceFormPage />} />
            {/* Regional In-Person */}
            <Route path="/regional-in-person" element={<RegionalInPersonPage />} />
            <Route path="/regional-in-person/new" element={<RegionalInPersonFormPage />} />
            <Route path="/regional-in-person/:id" element={<RegionalInPersonDetailPage />} />
            <Route path="/regional-in-person/:id/edit" element={<RegionalInPersonFormPage />} />
            {/* 1:Many PPRW */}
            <Route path="/pprw" element={<PPRWPage />} />
            <Route path="/pprw/workshop-templates" element={<PPRWTemplateManagerPage />} />
            <Route path="/pprw/workshops/:id" element={<PPRWWorkshopDetailPage />} />
            <Route path="/pprw/:id" element={<PPRWDetailPage />} />
            {/* Content Development */}
            <Route path="/content-development" element={<ContentDevelopmentDashboardPage />} />
            <Route path="/content-items" element={<ContentItemsPage />} />
            <Route path="/content-projects/:id" element={<ContentProjectDetailPage />} />
            <Route path="/content-items/:id" element={<ContentItemDetailPage />} />
            <Route path="/content-pos" element={<ContentPOsPage />} />
            <Route path="/taxonomy" element={<TaxonomyPage />} />
            <Route path="/workback-template" element={<WorkbackTemplateSettingsPage />} />
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/platform/kb" element={<KnowledgeBasePage />} />
            <Route path="/platform/tasks/:id" element={<TaskDetailPage />} />
            <Route path="/platform/tasks/:id" element={<TaskDetailPage />} />
          </Route>
          
          {/* Redirect root to a role-aware landing */}
          <Route path="/" element={<RoleLanding />} />
          
          {/* Legacy redirects */}
          <Route path="/training-requests/*" element={<Navigate to="/training-projects" replace />} />
          
          {/* Catch all - role-aware landing */}
          <Route path="*" element={<RoleLanding />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
